/**
 * @bos/portal3 — WHAT A ROOM ASKS: the questions and advisories that belong to this room,
 * rendered above the artifact they are about.
 *
 * This is the visible half of "questions in context". A question carrying `stage: 'rules'`
 * used to render at the bottom of a 7,000-line log while the rules sat behind a rail click;
 * now it renders in the Rules room, with the rules underneath it. The ANSWER MACHINERY IS
 * UNTOUCHED — the same drafts, the same `runs.answer` batch, the same server outcomes
 * ({@link QuestionList}). Only the address changed.
 *
 * WHAT A ROOM SHOWS:
 *   - the open round's questions whose stage names this room, when the round is open;
 *   - the "Settled for you" advisories whose stage names this room, standing, always —
 *     they are the record of what the machine decided for the owner in THIS part of their
 *     app, and their correction doors are unchanged.
 *
 * WHAT A ROOM NEVER SHOWS: banked answers whose question drifted away. Those carry no stage
 * (a `CheckpointAnswer` is an id, a text and a note), so filing them under a room would be a
 * guess. They stay on the Overview, which says why.
 */
import type {
  AnswerRunResult,
  CheckpointAnswer,
  CheckpointQuestion,
  RunIntakeSurface,
  RunQuestionsResult,
} from '@bos/portal3-server';
import { AdvisoriesBlock, QuestionList } from './QuestionRound';
import { SubjectStrip } from './SubjectStrip';
import { countPhrase, questionsForRoom } from './questionRouting';

export interface RoomQuestionsProps {
  /** This room's slug — the routing key, and the strip's kind→room map key. */
  readonly room: string;
  /** The round in hand, or null when the run has none (or it has not loaded). */
  readonly questions: RunQuestionsResult | null;
  readonly onSubmit: (answers: CheckpointAnswer[]) => Promise<AnswerRunResult>;
  /** The run's grounded brief, when it has one. Pre-intake runs pass undefined and the
   *  strip renders nothing at all. */
  readonly intake: RunIntakeSurface | undefined;
  /** Has this room's artifact been derived? Drives the strip's honest mid-derivation fallback. */
  readonly artifactDerived: boolean;
  /** A finished build can record nothing more — the advisories go read-only and say so. */
  readonly runClosed: boolean;
}

export function RoomQuestions({
  room,
  questions,
  onSubmit,
  intake,
  artifactDerived,
  runClosed,
}: RoomQuestionsProps) {
  if (questions === null) return null;
  const { round, answers } = questions;

  const mine = questionsForRoom(round.questions, room);
  const myAdvisories = questionsForRoom(round.advisories ?? [], room);
  if (mine.length === 0 && myAdvisories.length === 0) return null;

  const stripFor = (q: CheckpointQuestion) => (
    <SubjectStrip q={q} intake={intake} room={room} artifactDerived={artifactDerived} />
  );

  // The round document's OWN status decides whether the questions are still open — the disk
  // truth, not the journal's. A closed round's questions are history and are not re-offered.
  const roundOpen = round.status === 'awaiting-answers';

  return (
    <section className="qroom" aria-label="What the machine asked about this">
      {mine.length > 0 && roundOpen && (
        <div className="qround qroom__round">
          <p className="qround__title">
            The machine stopped to ask about this — {countPhrase(mine.length)}
          </p>
          <p className="qround__here">
            {mine.length === 1 ? 'It is' : 'They are'} here because{' '}
            {mine.length === 1 ? 'it is' : 'they are'} about what is on this page. Answer{' '}
            {mine.length === 1 ? 'it' : 'them'} here; the rest of the round is on the Overview.
          </p>
          <QuestionList
            questions={mine}
            answers={answers}
            onSubmit={onSubmit}
            stripFor={stripFor}
          />
        </div>
      )}
      <AdvisoriesBlock
        advisories={myAdvisories}
        answers={answers}
        onSubmit={onSubmit}
        runClosed={runClosed}
        heading={`Settled for you here (${myAdvisories.length}) — what the machine assumed about this part`}
        intro="The machine settled these on its own while it worked on this part of your app. If one is wrong, correct it — this build re-derives with your words when it next runs."
        stripFor={stripFor}
      />
    </section>
  );
}
